import React, {useEffect, useRef, useState} from 'react';
import {FaEye, FaEyeSlash} from "react-icons/fa";
import {FaCircleCheck, FaCircleXmark} from "react-icons/fa6";
import projectUtils, {clearRequiredFieldError, resetRequiredFieldError} from "../../utils/project-utils.js";
import {useRequiredFieldError} from "../../hooks/use-required-field-error.js";

const InputPassword = ({setIsValid, col, id, name, label, value, defaultValue, disabled, onChange, onInput, readOnly, reference, autofocus, password, target, inputClass = "", labelClass = "", passwordComplexity = false, required = false, invalid = false}) => {
    const [type, setType] = useState("password");
    const [validClass, setValidClass] = useState("");
    const [showTooltip, setShowTooltip] = useState(false);
    const inputRef = useRef(null);
    const isRequired = required || (typeof label === "string" && label.trim().endsWith("*"));
    const labelText = typeof label === "string" ? label.replace(/\s*\*\s*$/, "") : label;
    const requiredFieldInvalid = useRequiredFieldError(inputRef, isRequired);
    const hasRequiredError = !disabled && !value?.trim() && (invalid || requiredFieldInvalid);

    const [passwordValidated, setPasswordValidated] = useState({
        lowerCondition: false,
        upperCondition: false,
        numberCondition: false,
        lengthCondition: false,
        specialCondition: false,
    });

    useEffect(() => {
        if (isRequired && value?.trim()) clearRequiredFieldError(inputRef.current);
    }, [value]);

    useEffect(() => {
        if (disabled) resetRequiredFieldError(inputRef.current);
    }, [disabled]);

    const classNameInput = "password-input block w-full px-2.5 pb-1 pt-1 text-sm text-gray-700 bg-transparent border-[1px] rounded-md border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer h-[32px] transition-[border-color,border-width] duration-150 ease-out";

    useEffect(() => {
        if (password !== undefined) {
            setValidClass(value ? password === value ? "is-valid" : "is-invalid" : "");
        }
    }, [password, value]);

    useEffect(() => {
        if (target === "password") {
            const passwordValue = projectUtils.removeAccents(value.trim());

            const lowerCaseLetters = /[a-z]/g;
            const upperCaseLetters = /[A-Z]/g;
            const numbers = /[0-9]/g;
            const specialCharacters = /[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g;

            const lowerCondition = passwordValue.match(lowerCaseLetters)?.length > 0;
            const upperCondition = passwordValue.match(upperCaseLetters)?.length > 0;
            const numberCondition = passwordValue.match(numbers)?.length > 0;
            const lengthCondition = passwordValue.length >= 8;
            const specialCondition = passwordValue.match(specialCharacters)?.length > 0;

            const isValidPassword = () => lowerCondition && upperCondition && numberCondition && lengthCondition && specialCondition;

            setPasswordValidated({
                lowerCondition,
                upperCondition,
                numberCondition,
                lengthCondition,
                specialCondition,
            });

            setValidClass(value ? (isValidPassword() ? "is-valid" : "is-invalid") : "");
            if (setIsValid) setIsValid(isValidPassword())
        }
    }, [target, value])

    return (
        <div className={`relative ${col} ${validClass}`}>
            <input
                id={id}
                name={name}
                placeholder={""}
                defaultValue={defaultValue}
                value={value}
                type={type}
                className={[classNameInput, validClass, inputClass, hasRequiredError ? "!border-[2px] !border-[#dc2626] focus:!border-[#dc2626]" : ""].join(" ")}
                disabled={disabled}
                onChange={onChange}
                onBlur={() => { if (target === "password") setShowTooltip(false); }}
                onFocus={() => { if (target === "password") setShowTooltip(true); }}
                onInput={onInput}
                readOnly={readOnly}
                autoFocus={autofocus}
                ref={(element) => {
                    inputRef.current = element;
                    if (typeof reference === "function") reference(element);
                    else if (reference) reference.current = element;
                }}
                required={isRequired}
                data-required-field={isRequired || undefined}
                aria-invalid={hasRequiredError}
            />

            <label htmlFor={id} style={{whiteSpace: "nowrap"}}
                   className={"password-label absolute text-sm px-1 text-gray-500 duration-300 transform -translate-y-4 scale-75 top-[5px] " +
                       "z-10 origin-[0] bg-white peer-placeholder-shown:-top-[2px] peer-placeholder-shown:scale-100 " +
                       "peer-placeholder-shown:translate-y-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-2.5 " +
                       "peer-focus:top-[5px] peer-focus:px-1 max-w-full truncate " + labelClass
                   }
            >
                {labelText}{isRequired ? <span className="ml-0.5">*</span> : null}
            </label>

            <span
                className={"text-gray-700 absolute right-[10px] left-auto top-0.5 translate-y-1/2 pointer-events-auto cursor-pointer " + labelClass}
                onClick={() => setType(type === "password" ? "text" : "password")}>
                {type === "password" ? <FaEye/> : <FaEyeSlash/>}
            </span>

            {
                showTooltip && passwordComplexity &&
                <Card wrapperClass="transition w-fit !absolute top-full mt-2 whitespace-nowrap z-[999] !rounded-lg ">
                    <p className="text-gray-500 font-semibold !text-[12px] mb-2">Requisitos de senha:</p>
                    <span className={"flex items-center gap-2 " + (passwordValidated.lengthCondition ? "text-[#198754]" : "text-[#dc4c64]")}>
                        {passwordValidated.lengthCondition ? <FaCircleCheck /> : <FaCircleXmark/>} No mínimo 8 caracteres
                    </span>
                    <span className={"flex items-center gap-2 " + (passwordValidated.upperCondition ? "text-[#198754]" : "text-[#dc4c64]")}>
                        {passwordValidated.upperCondition ? <FaCircleCheck /> : <FaCircleXmark/>} Letras maiúsculas
                    </span>
                    <span className={"flex items-center gap-2 " + (passwordValidated.lowerCondition ? "text-[#198754]" : "text-[#dc4c64]")}>
                        {passwordValidated.lowerCondition ? <FaCircleCheck /> : <FaCircleXmark/>} Letras minúsculas
                    </span>
                    <span className={"flex items-center gap-2 " + (passwordValidated.numberCondition ? "text-[#198754]" : "text-[#dc4c64]")}>
                        {passwordValidated.numberCondition ? <FaCircleCheck /> : <FaCircleXmark/>} Números
                    </span>
                    <span className={"flex items-center gap-2 " + (passwordValidated.specialCondition ? "text-[#198754]" : "text-[#dc4c64]")}>
                        {passwordValidated.specialCondition ? <FaCircleCheck /> : <FaCircleXmark/>} Símbolos
                    </span>
                </Card>
            }
        </div>
    );
};

const Card = ({children, title, isMultipleTitles = false, extraTitles = [""], wrapperClass = "", headerClass = "", bodyClass = "", headerClick, titleClass, extraTitleWrapperClass, contentRef, hidden = false, id}) => {
    return (
        <div id={id} className={"bg-white rounded-[0.5rem] shadow-none border border-[#ededed] print:!border-0 print:!border-none print:shadow-none relative " + wrapperClass} ref={contentRef} hidden={hidden}>
            {
                title &&
                <div className={"heading bg-gray-200 py-[5px] px-[20px] rounded-t-[0.5rem] border-b border-gray-300 flex justify-start items-center " + headerClass}>
                    <h5 className={"font-semibold text-[13px] text-[#676a6c] " + titleClass} onClick={headerClick}>{title}</h5>
                    {isMultipleTitles &&
                        <div className={extraTitleWrapperClass}>
                            {extraTitles.map(extraTitle => (
                                <h5 key={extraTitle} className={"font-semibold text-[13px] text-[#676a6c]"} onClick={headerClick}>{extraTitle}</h5>
                            ))}
                        </div>
                    }
                </div>
            }

            <div className={"body p-[10px] " + (bodyClass ?? "")}>
                {children}
            </div>
        </div>
    );
}

export default InputPassword;